import React, { useState, useEffect, useCallback } from "react";
import { Stethoscope, Loader2, HeartPulse, Moon, Activity, Heart, Zap, Link2, RefreshCw, KeyRound, ExternalLink, Check, Unlink, Send } from "lucide-react";
import { whoopStatus, whoopSaveCreds, whoopLogin, whoopMetrics, whoopDisconnect, drpittChat } from "../lib/api";
import { toast } from "sonner";

const Metric = ({ icon: Icon, label, value, suffix, tone = "cyan" }) => {
  const tones = { cyan: "text-[#00F0FF]", green: "text-emerald-400", amber: "text-amber-400", rose: "text-rose-400" };
  return (
    <div className="bg-[#0a1020] border border-[#00F0FF22] rounded-2xl p-4" data-testid={`drpitt-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-cyan-200/50 text-xs"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <p className={`text-2xl font-extrabold mt-1 ${tones[tone]}`}>{value == null ? "—" : value}<span className="text-sm text-cyan-200/40 font-normal">{value != null ? suffix : ""}</span></p>
    </div>
  );
};

const recoveryTone = (p) => (p == null ? "cyan" : p >= 67 ? "green" : p >= 34 ? "amber" : "rose");

export default function NexusDrPitt({ session }) {
  const userId = session.user_id;
  const [status, setStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cid, setCid] = useState("");
  const [sec, setSec] = useState("");
  const [saving, setSaving] = useState(false);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [thinking, setThinking] = useState(false);
  const [sid, setSid] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await whoopStatus(userId);
      setStatus(s);
      if (s.connected) {
        try { setMetrics(await whoopMetrics(userId)); } catch (e) {}
      }
    } catch (e) {} finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("whoop") === "connected") {
      toast.success("WHOOP connected! Dr. Pitt can see your health now.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (new URLSearchParams(window.location.search).get("whoop") === "error") {
      toast.error("WHOOP connection failed — check your keys and redirect URL.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  const saveCreds = async () => {
    if (!cid.trim() || !sec.trim()) { toast.error("Paste both the Client ID and Client Secret"); return; }
    setSaving(true);
    try { await whoopSaveCreds(userId, cid.trim(), sec.trim()); setCid(""); setSec(""); toast.success("Keys saved — now click Connect WHOOP"); load(); }
    catch (e) { toast.error("Couldn't save keys"); }
    finally { setSaving(false); }
  };

  const connect = async () => {
    try { const r = await whoopLogin(userId); window.location.href = r.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Add your WHOOP keys first"); }
  };

  const disconnect = async () => {
    try { await whoopDisconnect(userId); setMetrics(null); load(); toast.success("WHOOP disconnected"); }
    catch (e) {}
  };

  const send = async () => {
    const text = msg.trim();
    if (!text || thinking) return;
    setChat((c) => [...c, { role: "user", text }]);
    setMsg(""); setThinking(true);
    try {
      const r = await drpittChat(userId, text, sid || undefined);
      setSid(r.session_id);
      setChat((c) => [...c, { role: "pitt", text: r.reply }]);
    } catch (e) {
      setChat((c) => [...c, { role: "pitt", text: e?.response?.data?.detail || "I'm unavailable right now — try again shortly." }]);
    } finally { setThinking(false); }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="drpitt-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><Stethoscope className="w-6 h-6" /> Dr. Pitt</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Nexus's health assistant. Watches your WHOOP recovery, sleep and strain, and can call to book doctor visits or remind you about meds.</p>
          </div>
          {status?.connected && <button onClick={load} data-testid="drpitt-refresh" className="text-cyan-200/60 hover:text-[#00F0FF] p-2"><RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} /></button>}
        </div>

        {loading && !status ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" /></div>
        ) : status?.connected ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="drpitt-metrics">
              <Metric icon={HeartPulse} label="Recovery" value={metrics?.recovery_percent} suffix="%" tone={recoveryTone(metrics?.recovery_percent)} />
              <Metric icon={Moon} label="Sleep" value={metrics?.sleep_hours} suffix=" h" />
              <Metric icon={Activity} label="Sleep score" value={metrics?.sleep_performance} suffix="%" />
              <Metric icon={Zap} label="Day strain" value={metrics?.day_strain} suffix="" tone="amber" />
              <Metric icon={Heart} label="Resting HR" value={metrics?.resting_hr} suffix=" bpm" tone="rose" />
              <Metric icon={HeartPulse} label="HRV" value={metrics?.hrv_ms} suffix=" ms" tone="green" />
            </div>
            {metrics?.recovery_percent != null && (
              <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-4" data-testid="drpitt-advice">
                <p className="text-cyan-100 text-sm leading-relaxed">
                  <b className="text-[#00F0FF]">Dr. Pitt says:</b>{" "}
                  {metrics.recovery_percent >= 67
                    ? "You're well recovered — a good day to push hard if you want. Stay hydrated."
                    : metrics.recovery_percent >= 34
                      ? "You're moderately recovered. Train smart, keep it measured, and prioritise sleep tonight."
                      : "You're in the red — your body needs rest today. Go easy, hydrate, and get to bed early."}
                </p>
              </div>
            )}
            <button onClick={disconnect} data-testid="drpitt-disconnect" className="text-cyan-200/40 hover:text-rose-300 text-xs flex items-center gap-1"><Unlink className="w-3.5 h-3.5" /> Disconnect WHOOP</button>
          </>
        ) : (
          <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-5 space-y-4" data-testid="drpitt-connect">
            {status?.credentials_set ? (
              <>
                <p className="text-cyan-100 text-sm flex items-center gap-2"><Check className="w-4 h-4 text-emerald-300" /> Your WHOOP keys are saved. One tap to connect:</p>
                <button onClick={connect} data-testid="drpitt-connect-btn" className="px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 flex items-center gap-2"><Link2 className="w-4 h-4" /> Connect WHOOP</button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-cyan-300/60" />
                  <span className="text-cyan-100 text-sm font-semibold">Connect your WHOOP</span>
                  <a href="https://developer-dashboard.whoop.com/" target="_blank" rel="noreferrer" className="ml-auto text-[#00F0FF] text-[11px] flex items-center gap-1 hover:underline">get keys <ExternalLink className="w-3 h-3" /></a>
                </div>
                <p className="text-cyan-200/50 text-xs leading-relaxed">
                  At developer-dashboard.whoop.com create an app, set this exact <b>Redirect URL</b>:
                </p>
                {status?.redirect_uri && (
                  <code className="block bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-2 text-[#00F0FF] text-xs break-all" data-testid="drpitt-redirect-uri">{status.redirect_uri}</code>
                )}
                <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="WHOOP Client ID" data-testid="drpitt-client-id"
                  className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-2 text-cyan-50 text-sm outline-none" />
                <input value={sec} onChange={(e) => setSec(e.target.value)} type="password" placeholder="WHOOP Client Secret" data-testid="drpitt-client-secret"
                  className="w-full bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-2 text-cyan-50 text-sm outline-none" />
                <button onClick={saveCreds} disabled={saving} data-testid="drpitt-save-keys" className="px-4 py-2 rounded-xl text-sm font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] disabled:opacity-50 flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save keys
                </button>
              </>
            )}
          </div>
        )}

        {/* Talk to Dr. Pitt (Muse-powered) */}
        <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-4 space-y-3" data-testid="drpitt-chat">
          <p className="text-cyan-100 text-sm font-semibold flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[#00F0FF]" /> Talk to Dr. Pitt</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {chat.length === 0 && (
              <p className="text-cyan-200/40 text-sm">Ask Dr. Pitt anything about your health, sleep, recovery — or say "book me a dentist cleaning" and he'll call for you.</p>
            )}
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`drpitt-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#00F0FF] text-[#030712] font-medium" : "bg-[#0a1020] border border-[#00F0FF22] text-cyan-50"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && <div className="flex justify-start"><div className="bg-[#0a1020] border border-[#00F0FF22] rounded-2xl px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-[#00F0FF]" /></div></div>}
          </div>
          <div className="flex items-center gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Dr. Pitt…" data-testid="drpitt-chat-input"
              className="flex-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-sm outline-none" />
            <button onClick={send} disabled={thinking || !msg.trim()} data-testid="drpitt-chat-send"
              className="px-4 py-2 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-1"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
